/**
 * DnAddProductModal — Pass 6
 *
 * Opens the shared validated Add Product form pre-populated with AI suggestions
 * derived from a Delivery Note line.  Nothing is auto-saved or auto-mapped;
 * the user must explicitly click "Save to Product Master".
 *
 * On save:  creates the beverage product master record (POST /api/beverage-products)
 *           then calls onProductCreated(newProduct).  The caller is responsible
 *           for surfacing the new product as a candidate; no line linkage occurs here.
 * On cancel: calls onCancel() with no writes.
 *
 * Data-privacy: source-line context strip shows only description, size and
 * supplier code.  Financial data (line_net, quantity, etc.) is never shown here.
 */
import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { getBackendUrl } from '@/lib/backendUrl';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button }    from '@/components/ui/button';
import { Badge }     from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, X, Sparkles, AlertTriangle, ExternalLink, Info } from 'lucide-react';
import {
  AddProductFormBody,
  emptyUnit,
  emptyProductBase,
  DEFAULT_UNIT_TYPES,
  DEFAULT_UNIT_LABELS,
} from './shared/AddProductFormBody';
import { resolveAITaxonomy } from '../pages/PsbStage1BPage';
import { useAuth } from '../contexts/AuthContext';

const API = getBackendUrl();
const DUPLICATE_REASON_MIN = 10;
const _DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 h

// ── Pack/case unit facts (Pass 7 gap 3) ─────────────────────────────────────
// Mirrors the gated "Task 7" pack-unit logic already proven in
// PsbStage1BPage.jsx's own Accept-AI handler (see reapply_case_unit.test.js):
// only applied on first Accept (no existing non-blank unit), only when the
// AI's own supporting_evidence substantiates the pack size — never invents a
// case count the source/AI did not state. Kept local to this modal (not a
// cross-file refactor of the 9000-line PSB page) so the existing PSB journey
// is untouched.
const _STANDARD_BOTTLE_ML = [200, 250, 275, 330, 440, 500, 568, 700, 750];
function snapToStandardBottleMl(ml) {
  if (!ml || ml <= 0) return ml;
  const exact = _STANDARD_BOTTLE_ML.find(s => Math.abs(s - ml) < 0.5);
  return exact !== undefined ? exact : ml;
}
// Spirits/wine/liqueurs are not normally purchased/counted by the case —
// same business rule already applied on the PSB side.
const _NON_CASE_CATEGORY_KEYWORDS = [
  'spirit', 'wine', 'liqueur', 'whisky', 'whiskey', 'gin', 'vodka',
  'rum', 'brandy', 'cognac', 'tequila', 'limoncello',
];
// AI confidence may arrive as a numeric 0-1 score or as a high/medium/low
// label (the prompt asks for the latter) — accept either honestly rather
// than silently failing a numeric-only comparison.
function _confidenceIsHighEnough(val) {
  if (val === null || val === undefined) return false;
  if (typeof val === 'number') return val >= 0.8;
  const s = String(val).trim().toLowerCase();
  return s === 'high' || s === 'medium';
}

// Same transient/UI-only unit keys stripped before POST in BeverageStockMasterPage.jsx.
const _TRANSIENT_UNIT_KEYS = ['_units_per_case', '_indiv_ml', '_orig_net_grams', '_orig_net_grams_evidence',
  'net_grams_reviewed_at', 'net_grams_reviewed_by_name', 'net_grams_reviewed_by_user_id', 'measure_type', '_customDraft', '_ai_unit_label'];

// ── small helpers ──────────────────────────────────────────────────────────────

function SourceStrip({ line }) {
  if (!line) return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 mb-3">
      <span className="font-semibold">From delivery note: </span>
      <span className="font-mono">{line.description_raw}</span>
      {line.size_raw && <span className="ml-2 text-amber-700">{line.size_raw}</span>}
      {line.product_code_raw && (
        <span className="ml-2 text-amber-600 italic">code: {line.product_code_raw}</span>
      )}
    </div>
  );
}

function AIPrefillBanner({ prefill, loading, onAccept, applied }) {
  if (loading) return (
    <div className="flex items-center gap-2 text-xs text-slate-500 py-1">
      <Loader2 className="h-3 w-3 animate-spin" />
      Loading AI suggestions…
    </div>
  );
  if (!prefill?.prefill) return null;
  const pf = prefill.prefill;
  const fields = [
    pf.suggested_name && `Name: ${pf.suggested_name}`,
    pf.brand          && `Brand: ${pf.brand}`,
    pf.subcategory    && `Category: ${pf.subcategory}`,
    pf.pack_label     && `Pack: ${pf.pack_label}`,
  ].filter(Boolean);
  if (!fields.length) return null;
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900 mb-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="inline-flex items-center gap-1 font-semibold">
            <Sparkles className="h-3 w-3" /> AI suggestion (advisory)
          </span>
          {prefill.from_cache && (
            <span className="ml-2 text-indigo-500 italic">cached</span>
          )}
          {prefill.latency_ms && !prefill.from_cache && (
            <span className="ml-2 text-indigo-400 italic">{prefill.latency_ms} ms</span>
          )}
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            {fields.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
          {prefill.advisory && (
            <p className="mt-1 text-indigo-600 italic">{prefill.advisory}</p>
          )}
        </div>
        {!applied && (
          <Button size="sm" variant="outline"
            className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100 shrink-0"
            onClick={onAccept} data-testid="dn-accept-ai-prefill">
            Accept
          </Button>
        )}
        {applied && (
          <Badge className="bg-indigo-100 text-indigo-700 border-indigo-300 text-xs shrink-0">
            Applied
          </Badge>
        )}
      </div>
    </div>
  );
}

function DupCandidateList({ candidates, onSelect }) {
  if (!candidates?.length) return null;
  return (
    <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-2 mb-3">
      <p className="text-xs font-semibold text-orange-900 mb-2 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" /> Possible duplicate products found
      </p>
      <div className="space-y-1">
        {candidates.map((c, i) => (
          <button key={i} type="button"
            className="w-full text-left text-xs rounded px-2 py-1 bg-white border border-orange-200 hover:bg-orange-50 transition"
            onClick={() => onSelect(c)}
            data-testid={`dn-dup-candidate-${i}`}>
            <span className="font-medium">{c.product_name}</span>
            {c.brand && <span className="ml-1 text-slate-500">· {c.brand}</span>}
            {c.subcategory_name && <span className="ml-1 text-slate-400">· {c.subcategory_name}</span>}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-orange-700">
        Return to an existing product above, or enter a reason below if this is genuinely different.
      </p>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function DnAddProductModal({ line, onCancel, onProductCreated }) {
  const { user } = useAuth();

  // ── Draft recovery ──────────────────────────────────────────────────────────
  const draftEnabled = !!(user?.id && user?.organisation_id && line?.id);
  const DRAFT_KEY = draftEnabled
    ? `dn_draft_v1_${user.id}_${user.organisation_id}_line_${line.id}`
    : null;
  const _isDraftFresh = (raw) => {
    try {
      if (!raw) return false;
      const { savedAt } = JSON.parse(raw);
      return !!savedAt && (Date.now() - savedAt < _DRAFT_TTL_MS);
    } catch { return false; }
  };

  const [form, setForm] = useState(() => ({
    ...emptyProductBase(),
    product_name: (line?.description_raw || '').trim(),
    units: [{ ...emptyUnit(), is_default_count_unit: true, is_default_purchase_unit: true }],
  }));
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [dupCandidates, setDupCandidates] = useState([]);
  const [dupReason, setDupReason]     = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [dynamicUnitTypes, setDynamicUnitTypes]   = useState(DEFAULT_UNIT_TYPES);
  const [dynamicUnitLabels, setDynamicUnitLabels] = useState(DEFAULT_UNIT_LABELS);
  const [dynamicUnitMlMap, setDynamicUnitMlMap]   = useState({});
  const [aiPrefill, setAiPrefill]     = useState(null);
  const [aiLoading, setAiLoading]     = useState(false);
  const [aiApplied, setAiApplied]     = useState(false);
  const [draftBannerOpen, setDraftBannerOpen] = useState(() => {
    if (!DRAFT_KEY) return false;
    const raw = localStorage.getItem(DRAFT_KEY);
    return _isDraftFresh(raw);
  });

  // Auto-save draft on form change
  useEffect(() => {
    if (!DRAFT_KEY || draftBannerOpen) return;
    if (!form.product_name && !form.units?.[0]?.unit_type) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, savedAt: Date.now() })); }
    catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // Clear dup list when name/brand changes
  useEffect(() => { setDupCandidates([]); }, [form.product_name, form.brand]);

  // Load taxonomy + measures
  useEffect(() => {
    api.get('/product-taxonomy/categories').then(r => {
      const cats = (r.data?.categories || []).filter(c => c.is_active).map(c => ({
        id: c.id, slug: c.slug, label: c.name, is_system: c.is_system,
        subcategories: (c.subcategories || []).filter(s => s.is_active),
      }));
      setCategoryOptions(cats);
    }).catch(() => {});
    api.get('/stocktake/measure-definitions').then(r => {
      const defs = r.data || [];
      if (defs.length > 0) {
        const sorted = [...defs].sort((a, b) => (a.name||'').localeCompare(b.name||'', undefined, {sensitivity:'base'}));
        const types = sorted.map(m => m.name.toLowerCase().replace(/\s+/g,'_'));
        const labels = {}, mlMap = {};
        sorted.forEach(m => {
          const key = m.name.toLowerCase().replace(/\s+/g,'_');
          labels[key] = m.name;
          if (m.default_ml != null && Number(m.default_ml) > 0) mlMap[key] = Number(m.default_ml);
        });
        setDynamicUnitTypes(types);
        setDynamicUnitLabels(labels);
        setDynamicUnitMlMap(mlMap);
      }
    }).catch(() => {});
  }, []);

  // Auto-fetch AI prefill on open
  useEffect(() => {
    if (!line?.id) return;
    setAiLoading(true);
    api.get(`/stocktake/delivery-documents/lines/${line.id}/ai-prefill`)
      .then(r => setAiPrefill(r.data))
      .catch(() => setAiPrefill(null))
      .finally(() => setAiLoading(false));
  }, [line?.id]);

  // ── Accept AI suggestions ────────────────────────────────────────────────────
  const handleAcceptAI = () => {
    if (!aiPrefill?.prefill) return;
    const pref = aiPrefill.prefill;
    const resolved = resolveAITaxonomy(pref, categoryOptions);
    // Evidence gate — same principle as the PSB side: never silently apply a
    // pack/case guess with nothing substantiating it.
    const citations = aiPrefill?.citations || pref.supporting_evidence || [];
    const bpcConfidenceOk = _confidenceIsHighEnough(pref.confidence?.bottles_per_case);

    setForm(f => {
      const updates = { ...f };
      const nameIsDefault = !f.product_name?.trim() || f.product_name.trim() === (line?.description_raw||'').trim();
      if (pref.suggested_name && nameIsDefault) updates.product_name = pref.suggested_name;
      if (pref.brand && !f.brand?.trim()) updates.brand = pref.brand;
      if (resolved) {
        if (!f.category_id) {
          updates.category_id    = resolved.categoryId;
          updates.category       = resolved.category;
          updates.subcategory    = resolved.subcategoryName;
          updates.subcategory_id = resolved.subcategoryId;
        } else if (resolved.categoryId === f.category_id && !f.subcategory_id) {
          updates.subcategory    = resolved.subcategoryName;
          updates.subcategory_id = resolved.subcategoryId;
        }
        if (resolved.aiSuggestedSubcategory && !resolved.subcategoryId)
          updates._aiSuggestedSubcategory = resolved.aiSuggestedSubcategory;
        else
          updates._aiSuggestedSubcategory = null;
      }

      // ── Pack facts (Pass 7 gap 3): preserve EA (count) and CS (purchase)
      // semantics instead of collapsing everything onto one "bottle" row.
      // Only runs on first Accept (first unit still blank) and only when the
      // AI cited evidence for the pack size — an unevidenced guess is left
      // for the user to fill in manually, never silently applied.
      const firstUnit = f.units?.[0] || { ...emptyUnit() };
      const packMl = parseFloat(pref.pack_qty ?? pref.pack_ml);
      if (packMl && citations.length > 0 && !firstUnit.unit_type && !firstUnit.conversion_to_base) {
        const bottleMl   = snapToStandardBottleMl(packMl);
        const packType   = (pref.pack_label || pref.pack_type || 'bottle').toLowerCase().replace(/\s+/g,'_');
        const rawCatLc   = (pref.category || '').toLowerCase();
        const isNonCase  = _NON_CASE_CATEGORY_KEYWORDS.some(k => rawCatLc.includes(k));
        const bpc        = Number(pref.bottles_per_case);
        const hasCaseFacts = Number.isFinite(bpc) && bpc > 1 && bpcConfidenceOk && !isNonCase;

        const eaUnit = {
          ...firstUnit, unit_type: packType, conversion_to_base: String(bottleMl),
          // EA (the individual bottle/can) is always the Count unit; it is
          // also the Purchase unit UNLESS verified case facts say otherwise.
          is_default_count_unit: true,
          is_default_purchase_unit: !hasCaseFacts,
        };
        updates.units = [eaUnit, ...f.units.slice(1)];

        if (hasCaseFacts) {
          const caseTotalMl = Math.round(bottleMl * bpc);
          updates.units = [
            ...updates.units,
            {
              ...emptyUnit(),
              unit_type: 'case', base_unit: 'ml',
              conversion_to_base: String(caseTotalMl),
              is_default_purchase_unit: true,  // CS — deliveries land in this unit
              is_default_count_unit:    false, // EA above remains the count unit
              _units_per_case: String(bpc),
              _indiv_ml:       String(bottleMl),
            },
          ];
        }
      }
      return updates;
    });
    setAiApplied(true);
  };

  // ── Draft helpers ────────────────────────────────────────────────────────────
  const restoreDraft = () => {
    if (!DRAFT_KEY) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (_isDraftFresh(raw)) setForm(JSON.parse(raw).form);
    } catch {}
    setDraftBannerOpen(false);
  };
  const discardDraft = () => {
    if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch {} }
    setDraftBannerOpen(false);
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (loading) return;
    if (!form.subcategory_id) { setError('Please select a subcategory.'); return; }
    const purchaseUnit = form.units.find(u => u.is_default_purchase_unit && u.unit_type && u.conversion_to_base);
    if (!purchaseUnit) { setError('At least one unit must have the Purchase role with a type and size value.'); return; }
    if (dupCandidates.length > 0 && dupReason.trim().length < DUPLICATE_REASON_MIN) {
      setError(`This may already exist — select an existing product above, or give a reason (min ${DUPLICATE_REASON_MIN} characters) to continue.`);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const salesUnit = form.units.find(u => (u.is_default_sales_unit || u.is_sales_unit) && u.conversion_to_base);
      const purchaseBase = purchaseUnit.base_unit || 'ml';
      const sourceNote = [
        `DN line ${line?.id}: "${(line?.description_raw||'').trim()}"`,
        line?.size_raw ? `Size: ${line.size_raw}` : null,
        line?.product_code_raw ? `Supplier code: ${line.product_code_raw}` : null,
      ].filter(Boolean).join(' | ');

      // Sanitize units for the backend (Pass 7 fix): emptyUnit() leaves optional
      // numeric fields as '' for the UI, but the backend requires a valid number
      // or null. Mirrors BeverageStockMasterPage.jsx's established _stripUnit +
      // numeric-coercion pattern exactly, so "Save to Product Master" no longer
      // 422s on every save with an unfilled waste%/obs-per-unit/net grams cell.
      const unitsForSave = form.units.filter(u => u.unit_type?.trim()).map(u => {
        const out = { ...u };
        _TRANSIENT_UNIT_KEYS.forEach(k => delete out[k]);
        return {
          ...out,
          conversion_to_base: u.conversion_to_base ? parseFloat(u.conversion_to_base) : null,
          waste_factor: u.waste_factor !== '' && u.waste_factor != null ? parseFloat(u.waste_factor) : null,
          observed_servings_per_unit: u.observed_servings_per_unit !== '' && u.observed_servings_per_unit != null ? parseFloat(u.observed_servings_per_unit) : null,
          net_grams: u.net_grams !== '' && u.net_grams != null ? parseFloat(u.net_grams) : null,
          net_grams_evidence: (u.net_grams_evidence || '').trim() || null,
        };
      });

      const body = {
        product_name:   form.product_name.trim(),
        brand:          form.brand?.trim() || null,
        category_id:    form.category_id ? Number(form.category_id) : undefined,
        category:       form.category    || undefined,
        subcategory_id: Number(form.subcategory_id),
        subcategory:    form.subcategory  || undefined,
        barcode:        form.barcode      || null,
        product_code:   form.product_code || null,
        internal_note:  `Created from delivery note · ${sourceNote}`,
        status:         'active',
        purchase_unit_ml:    Number(purchaseUnit.conversion_to_base),
        purchase_unit_label: `${purchaseUnit.unit_type} ${purchaseUnit.conversion_to_base}${purchaseBase}`,
        selling_unit_ml:     salesUnit ? Number(salesUnit.conversion_to_base) : null,
        selling_unit_label:  salesUnit ? salesUnit.unit_type : null,
        units: unitsForSave,
        ...(dupCandidates.length > 0
          ? { force_create: true, duplicate_override_reason: dupReason.trim() }
          : {}),
      };

      const { data } = await api.post('/beverage-products', body);
      if (DRAFT_KEY) { try { localStorage.removeItem(DRAFT_KEY); } catch {} }
      onProductCreated(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && Array.isArray(detail.candidates)) {
        setDupCandidates(detail.candidates);
        setError(detail.message);
      } else {
        setError(
          typeof detail === 'string'
            ? detail
            : (detail?.message || (detail ? JSON.stringify(detail) : null) || err.message || 'Unknown error')
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // No writes; return to source line
    onCancel();
  };

  const namePrefillBadge = aiPrefill?.prefill?.suggested_name ? (
    <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1 py-0.5">
      <Sparkles className="h-2.5 w-2.5" /> AI
    </span>
  ) : null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) handleCancel(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="dn-add-product-modal">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold flex items-center gap-2">
            Add to Product Master
            <Badge variant="outline" className="text-xs text-slate-500 font-normal">
              unsaved — review before saving
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Draft recovery banner */}
        {draftBannerOpen && (
          <Alert className="border-amber-300 bg-amber-50 mb-2">
            <Info className="h-4 w-4 text-amber-600" />
            <AlertDescription className="flex items-center justify-between gap-2 text-xs">
              <span className="text-amber-800">You have an unsaved draft for this line.</span>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" className="text-xs h-7"
                  onClick={restoreDraft} data-testid="dn-draft-restore">Restore</Button>
                <Button size="sm" variant="ghost" className="text-xs h-7"
                  onClick={discardDraft} data-testid="dn-draft-discard">Discard</Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Source-line context strip */}
        <SourceStrip line={line} />

        {/* AI prefill banner */}
        <AIPrefillBanner
          prefill={aiPrefill} loading={aiLoading}
          onAccept={handleAcceptAI} applied={aiApplied}
        />

        {/* Duplicate candidates */}
        <DupCandidateList
          candidates={dupCandidates}
          onSelect={(c) => { onCancel(); /* caller should navigate to c */ }}
        />
        {dupCandidates.length > 0 && (
          <div className="mb-3">
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Reason this is a different product (required to continue):
            </label>
            <input
              className="w-full border rounded px-2 py-1 text-xs"
              value={dupReason}
              onChange={e => setDupReason(e.target.value)}
              placeholder={`At least ${DUPLICATE_REASON_MIN} characters…`}
              data-testid="dn-dup-reason"
            />
          </div>
        )}

        {/* Error banner */}
        {error && (
          <Alert variant="destructive" className="mb-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {/* Core form */}
        <form onSubmit={handleSubmit}>
          <AddProductFormBody
            form={form}
            setForm={setForm}
            categoryOptions={categoryOptions}
            dynamicUnitTypes={dynamicUnitTypes}
            dynamicUnitLabels={dynamicUnitLabels}
            dynamicUnitMlMap={dynamicUnitMlMap}
            namePrefillBadge={namePrefillBadge}
          />

          {/* AI advisory note */}
          {aiPrefill?.note && (
            <p className="mt-2 text-xs text-slate-400 italic flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />{aiPrefill.note}
            </p>
          )}

          {/* Action row */}
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
            <Button type="button" variant="ghost" onClick={handleCancel}
              data-testid="dn-add-product-cancel" disabled={loading}>
              Cancel — return to line
            </Button>
            <Button type="submit"
              data-testid="dn-add-product-save" disabled={loading}
              className="min-w-[180px]">
              {loading
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                : 'Save to Product Master'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
